import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { titleize } from 'underscore.string';
import _ from 'lodash';
import { Map } from 'components/shipment/maps/map';
import Alert from 'components/ui/Alert';
import ErrorAlert from 'components/ui/ErrorAlert';
import ProTip from 'components/ui/ProTip';
import { Translation, withTranslation } from 'react-i18next';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { getEmptyLeg } from 'helpers/trip-helper';
import InfoIcon from 'components/v2/icons/InfoIcon';
import Leg from './Leg';
import TripPlannerEquipmentMatrix from './TripPlannerEquipmentMatrix';
const center = {
    lat: 34.0522,
    lng: -118.2437,
}; // Temp until we implement the map features
const equipmentTip = (React.createElement(Tooltip, { id: "equipments-tip" },
    React.createElement(Translation, null, (t) => t('TRIP_EQUIPMENT_TIP'))));
class TripPlannerForm extends Component {
    constructor(props) {
        super(props);
        this.handleCloseErrorAlert = () => this.setState({
            validationResult: {
                hasErrors: false,
                legs: [],
            },
        });
        this.handleCloseSaveErrorAlert = () => this.setState((prevState) => {
            const { formStatus: currentFormStatus } = prevState;
            const formStatus = Object.assign(Object.assign({}, currentFormStatus), { completed: false, error: null });
            return {
                formStatus,
            };
        });
        this.handleCloseSuccessAlert = () => this.setState((prevState) => {
            const { formStatus: currentFormStatus } = prevState;
            const formStatus = Object.assign(Object.assign({}, currentFormStatus), { completed: false });
            return {
                formStatus,
            };
        });
        this.handleSaveClick = () => {
            this.setState((prevState) => {
                const validationResult = this.validate(prevState);
                return {
                    validationResult,
                };
            }, () => {
                if (!this.state.validationResult.hasErrors) {
                    const payload = this.transformPayload();
                    this.props.onUpdateTrip(payload);
                }
            });
        };
        this.handleSaveAsClick = () => this.setState({
            showSaveAsSection: true,
        });
        this.handleSaveTrip = () => {
            this.setState((prevState) => {
                const validationResult = this.validate(prevState);
                return {
                    validationResult,
                };
            }, () => {
                if (!this.state.validationResult.hasErrors) {
                    const payload = this.transformPayload();
                    if (!payload._id) {
                        this.props.onCreateTrip(payload);
                    }
                    else {
                        this.props.onUpdateTrip(payload);
                    }
                }
            });
        };
        this.handleSaveAndApplyTrip = () => {
            this.setState((prevState) => {
                const validationResult = this.validate(prevState, true);
                return {
                    validationResult,
                };
            }, () => {
                if (!this.state.validationResult.hasErrors) {
                    const payload = this.transformPayload();
                    this.props.onSaveAndApplyTrip(payload);
                }
            });
        };
        this.handleTripNameChange = (e) => {
            const { target: { value: name }, } = e;
            this.setState((prevState) => ({
                trip: Object.assign(Object.assign({}, prevState.trip), { name }),
            }));
        };
        this.handleLegEquipmentAdd = (legSequence, equipmentId) => {
            this.setState((prevState) => {
                const { trip: { legs }, } = prevState;
                const index = legs.findIndex((leg) => leg.sequence === legSequence);
                const leg = _.cloneDeep(legs[index]);
                const equipment = this.props.equipments.find((eq) => eq._id === equipmentId);
                leg.stops = leg.stops.map((stop) => {
                    const stopEquipments = stop.equipments || [];
                    if (!stopEquipments.find((eq) => eq._id === equipmentId)) {
                        return Object.assign(Object.assign({}, stop), { equipments: [...stopEquipments, equipment] });
                    }
                    return stop;
                });
                return {
                    trip: Object.assign(Object.assign({}, prevState.trip), { legs: [...legs.slice(0, index), leg, ...legs.slice(index + 1)] }),
                };
            });
        };
        this.handleCarrierFieldChange = (legSequence, name) => {
            this.setState((prevState) => {
                const { trip: { legs }, } = prevState;
                const index = legs.findIndex((l) => l.sequence === legSequence);
                const leg = _.cloneDeep(legs[index]);
                leg.carrier = name;
                leg.carrier_id = null;
                return {
                    trip: Object.assign(Object.assign({}, prevState.trip), { legs: [...legs.slice(0, index), leg, ...legs.slice(index + 1)] }),
                };
            });
        };
        this.handleCarrierRemove = (legSequence) => this.setState((prevState) => {
            const { trip: { legs }, } = prevState;
            const index = legs.findIndex((l) => l.sequence === legSequence);
            const leg = _.cloneDeep(legs[index]);
            leg.carrier = '';
            leg.carrier_id = null;
            return {
                trip: Object.assign(Object.assign({}, prevState.trip), { legs: [...legs.slice(0, index), leg, ...legs.slice(index + 1)] }),
            };
        });
        this.handleCarrierSelect = (legSequence, carrier) => {
            this.setState((prevState) => {
                const { trip: { legs }, } = prevState;
                const index = legs.findIndex((l) => l.sequence === legSequence);
                const leg = _.cloneDeep(legs[index]);
                leg.carrier = carrier.name;
                leg.carrier_id = carrier._id;
                return {
                    trip: Object.assign(Object.assign({}, prevState.trip), { legs: [...legs.slice(0, index), leg, ...legs.slice(index + 1)] }),
                };
            });
        };
        this.handleLegAdd = () => this.setState((prevState) => ({
            trip: Object.assign(Object.assign({}, prevState.trip), { legs: [
                    ...prevState.trip.legs,
                    getEmptyLeg({
                        carrier: this.props.carrier,
                        sequence: prevState.trip.legs.length + 1,
                    }),
                ] }),
        }));
        this.handleLegRemove = (legSequence) => this.setState((prevState) => {
            const { trip: { legs: currentLegs }, } = prevState;
            const index = currentLegs.findIndex((l) => l.sequence === legSequence);
            const legs = [
                ...currentLegs.slice(0, index),
                ...currentLegs.slice(index + 1),
            ].map((leg, i) => (Object.assign(Object.assign({}, leg), { sequence: i + 1 })));
            return {
                trip: Object.assign(Object.assign({}, prevState.trip), { legs }),
            };
        });
        this.handleLegTypeChange = (legSequence, shipment_type) => {
            this.setState((prevState) => {
                const { trip: { legs }, } = prevState;
                const index = legs.findIndex((l) => l.sequence === legSequence);
                const leg = _.cloneDeep(legs[index]);
                leg.shipment_type = shipment_type;
                const stops = leg.stops || [];
                if (shipment_type === 'rail' && stops.length === 3) {
                    const lastStop = leg.stops[2];
                    const location = lastStop.location || { name: '', address: '' };
                    const stopFilled = location.name || location.address || lastStop.equipments.length > 0;
                    if (!stopFilled) {
                        leg.stops = [...leg.stops.slice(0, 2)];
                    }
                }
                else if (shipment_type === 'drayage' && stops.length === 2) {
                    leg.stops = [
                        ...leg.stops,
                        {
                            sequence: 3,
                            action: 'deliver',
                            empty: true,
                            equipments: [],
                            location: [],
                        },
                    ];
                }
                return {
                    trip: Object.assign(Object.assign({}, prevState.trip), { legs: [...legs.slice(0, index), leg, ...legs.slice(index + 1)] }),
                };
            });
        };
        this.handleStopAdd = (legSequence) => {
            this.setState((prevState) => {
                const { trip: { legs }, } = prevState;
                const legIndex = legs.findIndex((l) => l.sequence === legSequence);
                const leg = _.cloneDeep(legs[legIndex]);
                const sequence = leg.stops.length + 1;
                leg.stops = [
                    ...leg.stops,
                    {
                        sequence,
                        action: '',
                        equipments: [],
                        location: [
                            {
                                name: '',
                                address: '',
                            },
                        ],
                    },
                ];
                return {
                    trip: Object.assign(Object.assign({}, prevState.trip), { legs: [...legs.slice(0, legIndex), leg, ...legs.slice(legIndex + 1)] }),
                };
            });
        };
        this.handleStopEquipmentAdd = (legSequence, stopSequence, equipmentId) => {
            this.setState((prevState) => {
                const { trip: { legs }, } = prevState;
                const index = legs.findIndex((leg) => leg.sequence === legSequence);
                const leg = _.cloneDeep(legs[index]);
                const stopIndex = leg.stops.findIndex((stop) => stop.sequence === stopSequence);
                const stop = Object.assign({}, leg.stops[stopIndex]);
                const equipments = stop.equipments || [];
                if (equipments.findIndex((eq) => eq._id === equipmentId) === -1) {
                    const equipment = this.props.equipments.find((eq) => eq._id === equipmentId);
                    stop.equipments = equipments.concat(equipment);
                    leg.stops = [
                        ...leg.stops.slice(0, stopIndex),
                        stop,
                        ...leg.stops.slice(stopIndex + 1),
                    ];
                    return {
                        trip: Object.assign(Object.assign({}, prevState.trip), { legs: [...legs.slice(0, index), leg, ...legs.slice(index + 1)] }),
                    };
                }
                return null;
            });
        };
        this.handleStopEquipmentRemove = (legSequence, stopSequence, equipmentId) => {
            this.setState((prevState) => {
                const { trip: { legs }, } = prevState;
                const index = legs.findIndex((leg) => leg.sequence === legSequence);
                const leg = _.cloneDeep(legs[index]);
                const stopIndex = leg.stops.findIndex((stop) => stop.sequence === stopSequence);
                const stop = Object.assign({}, leg.stops[stopIndex]);
                const stopEquipments = stop.equipments || [];
                const equipmentIndex = stopEquipments.findIndex((eq) => eq._id === equipmentId);
                stop.equipments = [
                    ...stop.equipments.slice(0, equipmentIndex),
                    ...stop.equipments.slice(equipmentIndex + 1),
                ];
                leg.stops = [
                    ...leg.stops.slice(0, stopIndex),
                    stop,
                    ...leg.stops.slice(stopIndex + 1),
                ];
                return {
                    trip: Object.assign(Object.assign({}, prevState.trip), { legs: [...legs.slice(0, index), leg, ...legs.slice(index + 1)] }),
                };
            });
        };
        this.handleStopIntentSelect = (legSequence, stopSequence, intent) => {
            this.setState((prevState) => {
                const { trip: { legs }, } = prevState;
                const index = legs.findIndex((l) => l.sequence === legSequence);
                const leg = _.cloneDeep(legs[index]);
                const stopIndex = leg.stops.findIndex((s) => s.sequence === stopSequence);
                const stop = Object.assign(Object.assign({}, leg.stops[stopIndex]), intent);
                leg.stops = [
                    ...leg.stops.slice(0, stopIndex),
                    stop,
                    ...leg.stops.slice(stopIndex + 1),
                ];
                return {
                    trip: Object.assign(Object.assign({}, prevState.trip), { legs: [...legs.slice(0, index), leg, ...legs.slice(index + 1)] }),
                };
            });
        };
        this.handleStopLocationNameChange = (legSequence, stopSequence, value) => {
            this.setState((prevState) => {
                const { trip: { legs }, } = prevState;
                const index = legs.findIndex((l) => l.sequence === legSequence);
                const leg = _.cloneDeep(legs[index]);
                const stopIndex = leg.stops.findIndex((s) => s.sequence === stopSequence);
                const stop = leg.stops[stopIndex];
                stop.location = stop.location || { address: '', name: '' };
                stop.location.name = value;
                leg.stops = [
                    ...leg.stops.slice(0, stopIndex),
                    stop,
                    ...leg.stops.slice(stopIndex + 1),
                ];
                return {
                    trip: Object.assign(Object.assign({}, prevState.trip), { legs: [...legs.slice(0, index), leg, ...legs.slice(index + 1)] }),
                };
            });
        };
        this.handleStopLocationAddressChange = (legSequence, stopSequence) => {
            this.setState((prevState) => {
                const { trip: { legs }, } = prevState;
                const index = legs.findIndex((l) => l.sequence === legSequence);
                const leg = _.cloneDeep(legs[index]);
                const stopIndex = leg.stops.findIndex((s) => s.sequence === stopSequence);
                const stop = leg.stops[stopIndex];
                const location = stop.location;
                stop.location = Object.assign(Object.assign({}, location), { address: '' });
                leg.stops = [
                    ...leg.stops.slice(0, stopIndex),
                    stop,
                    ...leg.stops.slice(stopIndex + 1),
                ];
                return {
                    trip: Object.assign(Object.assign({}, prevState.trip), { legs: [...legs.slice(0, index), leg, ...legs.slice(index + 1)] }),
                };
            });
        };
        this.handleStopLocationSelect = (legSequence, stopSequence, item) => {
            this.setState((prevState) => {
                const { trip: { legs }, } = prevState;
                const index = legs.findIndex((l) => l.sequence === legSequence);
                const leg = _.cloneDeep(legs[index]);
                const stopIndex = leg.stops.findIndex((s) => s.sequence === stopSequence);
                const stop = leg.stops[stopIndex];
                const location = stop.location;
                const name = location.name || item.name;
                stop.location = Object.assign(Object.assign({}, item), { name });
                leg.stops = [
                    ...leg.stops.slice(0, stopIndex),
                    stop,
                    ...leg.stops.slice(stopIndex + 1),
                ];
                return {
                    trip: Object.assign(Object.assign({}, prevState.trip), { legs: [...legs.slice(0, index), leg, ...legs.slice(index + 1)] }),
                };
            });
        };
        this.handleStopRemove = (legSequence, stopSequence) => {
            this.setState((prevState) => {
                const { trip: { legs }, } = prevState;
                const index = legs.findIndex((l) => l.sequence === legSequence);
                const leg = _.cloneDeep(legs[index]);
                const stopIndex = leg.stops.findIndex((s) => s.sequence === stopSequence);
                leg.stops = [
                    ...leg.stops.slice(0, stopIndex),
                    ...leg.stops.slice(stopIndex + 1),
                ].map((stop, i) => (Object.assign(Object.assign({}, stop), { sequence: i + 1 })));
                return {
                    trip: Object.assign(Object.assign({}, prevState.trip), { legs: [...legs.slice(0, index), leg, ...legs.slice(index + 1)] }),
                };
            });
        };
        this.handleMatrixChange = (matrixData) => {
            const { trip: { legs }, } = this.state;
            let tempLegs = _.cloneDeep(legs);
            const eqIdsArr = Object.keys(matrixData);
            eqIdsArr.forEach((eqId) => {
                const eqMatrix = matrixData[eqId];
                const legsIdsArr = Object.keys(eqMatrix);
                legsIdsArr.forEach((legId) => {
                    const legStopsArr = matrixData[eqId][legId];
                    const foundIndex = tempLegs.findIndex((leg) => leg.sequence === Number(legId.replace(/\D+/g, '')));
                    const foundLeg = tempLegs[foundIndex];
                    let stops = foundLeg.stops.map((stop) => {
                        let equipments = [...stop.equipments];
                        if (legStopsArr[`stop_${stop.sequence}`] &&
                            equipments.every((eq) => eq.equipment_id !== eqId)) {
                            equipments.push(this.props.equipments.find((eq) => eq.equipment_id === eqId));
                        }
                        else if (legStopsArr[`stop_${stop.sequence}`] === false &&
                            equipments.some((eq) => eq.equipment_id === eqId)) {
                            equipments = equipments.filter((eq) => eq.equipment_id !== eqId);
                        }
                        return Object.assign(Object.assign({}, stop), { equipments });
                    });
                    tempLegs[foundIndex] = Object.assign(Object.assign({}, foundLeg), { stops });
                });
            });
            this.setState((prevState) => {
                return {
                    trip: Object.assign(Object.assign({}, prevState.trip), { legs: tempLegs }),
                };
            });
        };
        const trip = props.trip
            ? Object.assign({}, props.trip) : {
            name: '',
            legs: [
                getEmptyLeg({
                    carrier: props.carrier,
                    sequence: 1,
                }),
            ],
        };
        const { formStatus } = props;
        this.state = {
            trip,
            showSaveAsSection: false,
            validationResult: {
                hasErrors: false,
                legs: [],
            },
            formStatus,
        };
    }
    UNSAFE_componentWillReceiveProps(nextProps) {
        const { trip: currentTrip, formStatus: currentFormStatus } = this.props;
        const { trip: nextTrip, formStatus: nextFormStatus } = nextProps;
        if (currentTrip._id !== nextTrip._id) {
            const updatedTrip = Object.assign({}, nextProps.trip);
            this.setState({
                trip: updatedTrip,
            });
        }
        if (!_.isEqual(currentFormStatus, nextFormStatus)) {
            this.setState((prevState) => {
                const showSaveAsSection = nextFormStatus.success
                    ? false
                    : prevState.showSaveAsSection;
                return {
                    formStatus: nextFormStatus,
                    showSaveAsSection,
                };
            });
        }
    }
    getHeading() {
        const { trip: { _id, name }, t, } = this.props;
        if (_id) {
            return t('UPDATE_WITH_VALUE', { name });
        }
        return t('CREATE_NEW_TRIP');
    }
    saveDisabled() {
        const { trip: { name: tripName }, formStatus: { pending }, } = this.state;
        if (pending) {
            return true;
        }
        if (tripName) {
            return false;
        }
        return true;
    }
    validate(state, _isApplying = false) {
        const { trip: { legs }, } = state;
        const { t } = this.props;
        const validationResult = {};
        validationResult.legs = legs.reduce((runningErrors, leg) => {
            const { stops } = leg;
            const legErrors = {};
            legErrors.base = [];
            if (stops.length === 0) {
                legErrors.base = [t('ADD_ATLEAST_ONE_STOP')];
                validationResult.hasErrors = true;
            }
            else {
                let hasStopError = false;
                legErrors.stops = stops.map((stop) => {
                    const location = stop.location || { address: '', name: '' };
                    const stopErrors = {};
                    if (!stop.action) {
                        validationResult.hasErrors = true;
                        stopErrors.action = t('PLEASE_SELECT_INTENT');
                    }
                    if (!location.address) {
                        stopErrors.address = t('SET_AN_ADDRESS_FOR_STOP');
                        hasStopError = true;
                    }
                    return stopErrors;
                });
                if (hasStopError) {
                    legErrors.base.push(t('FILL_ALL_REQUIRED_FIELDS_FOR_STOPS'));
                    validationResult.hasErrors = true;
                }
            }
            return runningErrors.concat(legErrors);
        }, []);
        return validationResult;
    }
    transformPayload() {
        const { trip: { _id, name, legs: currentLegs }, } = this.state;
        const legs = currentLegs.map((leg) => {
            const { status, shipment_type, carrier_id, sequence: legSeqence, stops: legStops, } = leg;
            const carrier = carrier_id;
            const stops = legStops.map((stop) => {
                const { sequence, action, empty, location, equipments } = stop;
                return {
                    sequence,
                    action,
                    empty,
                    location,
                    equipments,
                    carrier,
                };
            });
            if (shipment_type === 'drayage') {
                return {
                    sequence: legSeqence,
                    shipment_type,
                    carrier,
                    carrier_id,
                    stops,
                    status,
                };
            }
            return {
                sequence: legSeqence,
                shipment_type,
                carrier,
                carrier_id,
                stops,
            };
        });
        const trip = {
            name,
            legs,
        };
        if (_id) {
            trip._id = _id;
        }
        return trip;
    }
    renderEmptyLegs() {
        const { t } = this.props;
        return (React.createElement("div", { className: "p-8 bg-white shadow rounded" },
            React.createElement("div", { className: "" },
                React.createElement("p", null, t('CREATE_LEG_TO_START')),
                React.createElement("button", { onClick: this.handleLegAdd, className: "button button-hollow-green" },
                    React.createElement("i", { className: "fa fa-plus mr-2" }),
                    titleize(t('LEG', { count: 1 }))))));
    }
    renderLegs() {
        const { trip: { legs }, } = this.state;
        if (legs.length === 0) {
            return this.renderEmptyLegs();
        }
        const { t, shipperId } = this.props;
        return (React.createElement("div", null,
            React.createElement("div", { className: "mb-6" }, legs.map((leg, i) => (React.createElement(Leg, { key: i, leg: leg, shipperId: shipperId, validationResult: this.state.validationResult.legs[i], onCarrierFieldChange: this.handleCarrierFieldChange, onCarrierRemove: this.handleCarrierRemove, onCarrierSelect: this.handleCarrierSelect, onLegEquipmentAdd: this.handleLegEquipmentAdd, onLegRemove: this.handleLegRemove, onLegTypeChange: this.handleLegTypeChange, onStatusChange: this.handleStatusChange, onStopAdd: this.handleStopAdd, onStopIntentSelect: this.handleStopIntentSelect, onStopEquipmentAdd: this.handleStopEquipmentAdd, onStopEquipmentRemove: this.handleStopEquipmentRemove, onStopLocationAddressChange: this.handleStopLocationAddressChange, onStopLocationNameChange: this.handleStopLocationNameChange, onStopLocationSelect: this.handleStopLocationSelect, onStopRemove: this.handleStopRemove })))),
            React.createElement("div", null,
                React.createElement("button", { onClick: this.handleLegAdd, className: "button button-hollow-green bg-white" },
                    React.createElement("i", { className: "fa fa-plus mr-2" }),
                    titleize(t('LEG', { count: 1 }))))));
    }
    renderEquipment() {
        const equipments = this.props.equipments || [];
        const { t } = this.props;
        const { trip: { legs }, } = this.state;
        if (!equipments.length) {
            return null;
        }
        return (React.createElement("div", null,
            React.createElement("div", { className: "flex items-center space-x-1" },
                React.createElement("h3", { className: "text-xl mt-0 mb-4" }, titleize(t('EQUIPMENT'))),
                React.createElement(OverlayTrigger, { placement: "top", overlay: equipmentTip },
                    React.createElement("div", { className: "mb-3" },
                        React.createElement(InfoIcon, { size: "medium" })))),
            React.createElement(TripPlannerEquipmentMatrix, { equipments: equipments, legs: legs, onChangeData: this.handleMatrixChange })));
    }
    renderCreateActionButtons() {
        const { formStatus: { pending }, } = this.state;
        const { t } = this.props;
        return (React.createElement("div", { className: "flex my-4" },
            React.createElement("input", { type: "text", placeholder: t('TRIP_NAME'), value: this.state.trip.name, className: "form-field mr-2", onChange: this.handleTripNameChange }),
            React.createElement("button", { type: "submit", className: "button button-hollow-green mr-2", disabled: this.saveDisabled(), onClick: this.handleSaveTrip },
                !pending && React.createElement("span", null, t('CREATE_TRIP')),
                pending && (React.createElement("div", { className: "flex items-center" },
                    React.createElement("i", { className: "fa fa-spin fa-circle-o-notch mr-2" }),
                    React.createElement("span", null,
                        t('SAVING'),
                        "...")))),
            React.createElement("button", { type: "submit", className: "button button-green mr-2", disabled: this.saveDisabled(), onClick: this.handleSaveAndApplyTrip },
                !pending && React.createElement("span", null, t('CREATE_AND_APPLY_TRIP')),
                pending && (React.createElement("div", { className: "flex items-center" },
                    React.createElement("i", { className: "fa fa-spin fa-circle-o-notch mr-2" }),
                    React.createElement("span", null,
                        t('SAVING'),
                        "..."))))));
    }
    renderUpdateActionButtons() {
        const { formStatus: { pending }, } = this.state;
        const { t } = this.props;
        return (React.createElement("div", { className: "flex my-4" },
            !this.state.showSaveAsSection && [
                React.createElement("button", { key: "save", type: "submit", className: "button button-hollow-green mr-2", onClick: this.handleSaveClick }, t('SAVE_TRIP')),
                React.createElement("button", { key: "saveas", type: "submit", className: "button button-hollow-green mr-2", disabled: this.saveDisabled(), onClick: this.handleSaveAsClick }, t('SAVE_TRIP_AS')),
            ],
            this.state.showSaveAsSection && (React.createElement("div", null,
                React.createElement("input", { type: "text", placeholder: t('TRIP_NAME'), value: this.state.trip.name, className: "form-field mr-2", onChange: this.handleTripNameChange }),
                React.createElement("button", { type: "submit", className: "button button-hollow-green mr-2", disabled: this.saveDisabled(), onClick: this.handleSaveClick },
                    !pending && React.createElement("span", null, t('SAVE_TRIP')),
                    pending && (React.createElement("div", { className: "flex items-center" },
                        React.createElement("i", { className: "fa fa-spin fa-circle-o-notch mr-2" }),
                        React.createElement("span", null,
                            t('SAVING'),
                            "...")))))),
            React.createElement("button", { key: "save-and-apply", type: "submit", className: "button button-green mr-2", disabled: this.saveDisabled(), onClick: this.handleSaveAndApplyTrip }, t('SAVE_TRIP_AND_APPLY_ORDER')),
            ","));
    }
    renderActions() {
        const { trip: { _id }, t, } = this.props;
        const { formStatus, validationResult } = this.state;
        return (React.createElement("div", { className: "flex flex-wrap my-4 -mx-4 justify-end" },
            React.createElement("div", { className: "flex-auto" },
                validationResult.hasErrors && (React.createElement("div", { className: "w-full" },
                    React.createElement(ErrorAlert, { title: validationResult.emptyStatus || t('FILL_ALL_REQUIRED_FIELDS'), canClose: true, onClose: this.handleCloseErrorAlert }))),
                formStatus.completed && !formStatus.success && (React.createElement("div", { className: "w-full" },
                    React.createElement(ErrorAlert, { title: t('TRIP_FAILED_TO_SAVE'), canClose: true, onClose: this.handleCloseSaveErrorAlert }))),
                formStatus.completed && formStatus.success && (React.createElement("div", { className: "w-full" },
                    React.createElement(Alert, { title: t('TRIP_SAVED_SUCCESSFULLY'), type: "success", canClose: true, onClose: this.handleCloseSuccessAlert })))),
            React.createElement("div", { className: "px-4 md:w-2/5 w-full" },
                React.createElement(ProTip, { message: t('SAVE_TRIP_PROTIPS') })),
            React.createElement("div", { className: "px-4" },
                _id && this.renderUpdateActionButtons(),
                !_id && this.renderCreateActionButtons())));
    }
    render() {
        const { t } = this.props;
        return (React.createElement("div", { className: "flex flex-col" },
            React.createElement("div", { className: "flex flex-wrap -mx-2" },
                React.createElement("div", { className: "md:w-2/3 lg:w-3/5 w-full px-2" },
                    React.createElement("h2", { className: "text-2xl mt-0 mb-4" }, this.getHeading()),
                    React.createElement("div", null, this.renderEquipment()),
                    React.createElement("div", { className: "overflow-scroll", style: { height: 'calc(100vh - 25rem)' } },
                        React.createElement("div", { className: "flex items-center" },
                            React.createElement("h3", { className: "text-xl mt-0 mb-2" }, titleize(t('LEG', { count: 2 }))),
                            React.createElement("button", { title: t('ADD_A_LEG'), className: "flex-grow-0 flex-shrink-0 ml-2 w-8 h-8 mb-4 border border-green border-solid hover:bg-green hover:text-white text-green rounded-full", onClick: this.handleLegAdd },
                                React.createElement("i", { className: "fa fa-plus mr-0" }))),
                        this.renderLegs())),
                React.createElement("div", { className: "md:w-1/3 lg:w-2/5 w-full px-2" },
                    React.createElement(Map, { googleMapURL: "https://maps.googleapis.com/maps/api/js?client=gme-cargomaticinc&v=3.exp&libraries=geometry,drawing,places", loadingElement: React.createElement("div", { className: "h-full" }), containerElement: React.createElement("div", { style: { height: '100%', width: '100%' } }), mapElement: React.createElement("div", { className: "h-full" }), waypoints: [], center: center }))),
            React.createElement("div", { className: "flex flex-col items-end" }, this.renderActions())));
    }
}
TripPlannerForm.propTypes = {
    carrier: PropTypes.shape({
        _id: PropTypes.string,
        name: PropTypes.string,
    }),
    equipments: PropTypes.array,
    shipperId: PropTypes.string,
    trip: PropTypes.shape({
        name: PropTypes.string,
        legs: PropTypes.arrayOf(PropTypes.shape({
            carrier_id: PropTypes.string,
            carrier: PropTypes.string,
            stops: PropTypes.arrayOf(PropTypes.shape({
                action: PropTypes.oneOf(['pickup', 'deliver']),
                empty: PropTypes.bool,
                location: PropTypes.shape({
                    address: PropTypes.string,
                    name: PropTypes.string,
                }),
            })),
        })),
    }),
    formStatus: PropTypes.shape({
        pending: PropTypes.bool.isRequired,
        completed: PropTypes.bool.isRequired,
        success: PropTypes.bool.isRequired,
        error: PropTypes.any,
    }).isRequired,
    onClose: PropTypes.func.isRequired,
    onCreateTrip: PropTypes.func.isRequired,
    onUpdateTrip: PropTypes.func.isRequired,
    onSaveAndApplyTrip: PropTypes.func.isRequired,
};
TripPlannerForm.defaultProps = {
    equipments: [],
    trip: {
        name: '',
        legs: [
            getEmptyLeg({
                sequence: 1,
            }),
        ],
    },
};
export default withTranslation()(TripPlannerForm);
